import React, { useState, useEffect, useRef } from 'react'
import { Bars3Icon, XMarkIcon, ChevronDownIcon, ChevronRightIcon, UserIcon, EnvelopeIcon } from '@heroicons/react/24/outline'
import useSWR from 'swr'
import fetcher from '@lib/fetcher'
import LoginModal from '../login-modal'
import spektrixAuth from '@lib/spektrix/auth'

const MegaMenu = ({ authState = { authenticated: false, user: null, loading: false } }) => {
  const [isOpen, setIsOpen] = useState(false)
  const [isMobile, setIsMobile] = useState(false)
  const [expandedItems, setExpandedItems] = useState({})
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false)
  const dropdownRef = useRef(null)

  // Fetch menu data
  const { data: menuData, error, isLoading } = useSWR('/menus', fetcher)
  const menus = menuData?.menus || []

  // Filter out What's On since we have a dedicated menu for it
  const filteredMenus = menus.filter(menu => 
    menu.name.toLowerCase() !== "what's on" && 
    menu.name.toLowerCase() !== "whats on"
  )

  const handleLogout = async () => {
    await spektrixAuth.logout()
    setIsOpen(false)
  }

  const handleLoginSuccess = () => {
    setIsLoginModalOpen(false)
  }

  // When a root menu is expanded, auto-expand all its children
  const toggleExpanded = (menuId) => {
    setExpandedItems(prev => {
      const newState = { ...prev }
      const isExpanding = !prev[menuId]
      
      // Toggle the clicked item
      newState[menuId] = isExpanding
      
      // If we're expanding a root menu, also expand all its children
      if (isExpanding) {
        const menu = findMenuById(filteredMenus, menuId)
        if (menu && menu.children) {
          const expandChildren = (items) => {
            items.forEach(child => {
              if (child.children && child.children.length > 0) {
                newState[child.id] = true
                expandChildren(child.children)
              }
            })
          }
          expandChildren(menu.children)
        }
      }
      
      return newState
    })
  }
  
  // Helper function to find menu by ID
  const findMenuById = (menus, id) => {
    for (const menu of menus) {
      if (menu.id === id) return menu
      if (menu.children) {
        const found = findMenuById(menu.children, id)
        if (found) return found
      }
    }
    return null
  }

  // Check if mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768)
    }
    checkMobile()
    window.addEventListener('resize', checkMobile)
    return () => window.removeEventListener('resize', checkMobile)
  }, [])

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])


  // Render mobile menu item with recursive 3rd level support
  const renderMobileMenuItem = (menu, level = 0) => {
    const hasChildren = menu.children && menu.children.length > 0
    const isExpanded = expandedItems[menu.id]
    const isRootLevel = level === 0

    return (
      <div key={menu.id} className={`${level > 0 ? 'ml-4' : ''}`}>
        {menu.is_header ? (
          <div>
            <div 
              className={`px-4 py-3 flex items-center justify-between cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 ${
                isRootLevel 
                  ? 'text-base font-bold font-serif text-gray-900 dark:text-white' 
                  : 'text-sm font-serif text-gray-700 dark:text-gray-300'
              }`}
              onClick={() => hasChildren && toggleExpanded(menu.id)}
            >
              <span>{menu.name}</span>
              {hasChildren && (
                <ChevronDownIcon 
                  className={`${isRootLevel ? 'w-5 h-5' : 'w-4 h-4'} transition-transform ${isExpanded ? 'rotate-180' : ''}`} 
                />
              )}
            </div>
            {hasChildren && isExpanded && (
              <div className="bg-gray-50 dark:bg-gray-800">
                {menu.children.map(child => renderMobileMenuItem(child, level + 1))}
              </div>
            )}
          </div>
        ) : (
          <div>
            <div className="flex items-center">
              {/* For root level items with children, make the whole row clickable for expand/collapse */}
              {isRootLevel && hasChildren ? (
                <button
                  className="flex items-center justify-between w-full px-4 py-3 text-base font-bold font-serif text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-800"
                  onClick={() => toggleExpanded(menu.id)}
                >
                  <span>{menu.name}</span>
                  <ChevronDownIcon 
                    className={`w-5 h-5 transition-transform ${isExpanded ? 'rotate-180' : ''}`} 
                  />
                </button>
              ) : (
                <a
                  href={menu.path || '#'}
                  className={`block px-4 ${isRootLevel ? 'py-3' : 'py-2'} ${
                    isRootLevel 
                      ? 'text-base font-bold font-serif text-gray-900 dark:text-white' 
                      : 'text-sm text-gray-700 dark:text-gray-300'
                  } hover:bg-gray-100 dark:hover:bg-gray-700 flex-grow`}
                  onClick={(e) => {
                    if (hasChildren && !menu.path) {
                      e.preventDefault()
                      toggleExpanded(menu.id)
                    } else {
                      setIsOpen(false)
                    }
                  }}
                >
                  {menu.name}
                </a>
              )}
            </div>
            {hasChildren && isExpanded && (
              <div className="bg-gray-50 dark:bg-gray-800">
                {menu.children.map(child => renderMobileMenuItem(child, level + 1))}
              </div>
            )}
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Menu Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center space-x-1 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors"
        aria-label="Toggle menu"
      >
        {isOpen ? (
          <XMarkIcon className="w-6 h-6" />
        ) : (
          <Bars3Icon className="w-6 h-6" />
        )}
      </button>

      {/* Mobile Menu - Full screen overlay */}
      {isMobile && isOpen && (
        <div className="fixed inset-0 z-50 bg-white dark:bg-gray-900">
          <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Menu</h2>
            <button
              onClick={() => setIsOpen(false)}
              className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>
          <div className="overflow-y-auto h-[calc(100vh-4rem)]">
            {filteredMenus.map(menu => renderMobileMenuItem(menu))}
            
            {/* Additional mobile menu items */}
            <div className="border-t border-gray-200 dark:border-gray-700 mt-4 pt-4">
              <a href="/memberships" className="block px-4 py-3 text-base font-bold font-serif text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-800">
                Memberships
              </a>
              <a href="/basket" className="block px-4 py-3 text-base font-bold font-serif text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-800">
                Basket
              </a>
              <a href="/mailing-list/signup" className="block px-4 py-3 text-base font-bold font-serif text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-800">
                <span className="flex items-center gap-2">
                  <EnvelopeIcon className="w-5 h-5" />
                  Join Mailing List
                </span>
              </a>

              {/* User Account Section */}
              <div className="border-t border-gray-200 dark:border-gray-700 mt-4 pt-4">
                {authState.authenticated && authState.user ? (
                  <>
                    <a href="/my-account" className="block px-4 py-3 text-base font-bold font-serif text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-800">
                      <span className="flex items-center gap-2">
                        <UserIcon className="w-5 h-5" />
                        My Account
                      </span>
                    </a>
                    <button
                      onClick={handleLogout}
                      className="block w-full text-left px-4 py-3 text-base font-bold font-serif text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-800"
                    >
                      Sign Out
                    </button>
                  </>
                ) : (
                  <button
                    onClick={() => {
                      setIsLoginModalOpen(true)
                      setIsOpen(false)
                    }}
                    className="block w-full text-left px-4 py-3 text-base font-bold font-serif text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-800"
                  >
                    <span className="flex items-center gap-2">
                      <UserIcon className="w-5 h-5" />
                      Sign In
                    </span>
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Desktop Menu - Docked to right */}
      {!isMobile && isOpen && (
        <div className="fixed inset-0 z-50">
          {/* Background overlay */}
          <div 
            className="absolute inset-0 bg-black/50 animate-fade-in" 
            onClick={() => setIsOpen(false)} 
          />
          
          {/* Menu panel */}
          <div className="absolute right-0 top-0 h-full w-full max-w-sm bg-white dark:bg-gray-900 shadow-xl animate-slide-in-right">
            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Menu</h2>
              <button
                onClick={() => setIsOpen(false)}
                className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>
            <div className="overflow-y-auto h-[calc(100vh-4rem)]">
              {filteredMenus.map(menu => renderMobileMenuItem(menu))}
              
              {/* Additional desktop menu items */}
              <div className="border-t border-gray-200 dark:border-gray-700 mt-4 pt-4">
                <a href="/memberships" className="block px-4 py-3 text-base font-bold font-serif text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-800">
                  Memberships
                </a>
                <a href="/basket" className="block px-4 py-3 text-base font-bold font-serif text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-800">
                  Basket
                </a>
                <a href="/mailing-list/signup" className="block px-4 py-3 text-base font-bold font-serif text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-800">
                  <span className="flex items-center gap-2">
                    <EnvelopeIcon className="w-5 h-5" />
                    Join Mailing List
                  </span>
                </a>

                {/* User Account Section */}
                <div className="border-t border-gray-200 dark:border-gray-700 mt-4 pt-4">
                  {authState.authenticated && authState.user ? (
                    <>
                      <a href="/my-account" className="block px-4 py-3 text-base font-bold font-serif text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-800">
                        <span className="flex items-center gap-2">
                          <UserIcon className="w-5 h-5" />
                          My Account
                        </span>
                      </a>
                      <button
                        onClick={handleLogout}
                        className="block w-full text-left px-4 py-3 text-base font-bold font-serif text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-800"
                      >
                        Sign Out
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={() => {
                        setIsLoginModalOpen(true)
                        setIsOpen(false)
                      }}
                      className="block w-full text-left px-4 py-3 text-base font-bold font-serif text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-800"
                    >
                      <span className="flex items-center gap-2">
                        <UserIcon className="w-5 h-5" />
                        Sign In
                      </span>
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Login Modal */}
      <LoginModal
        isOpen={isLoginModalOpen}
        onClose={() => setIsLoginModalOpen(false)}
        onLoginSuccess={handleLoginSuccess}
      />
    </div>
  )
}

export default MegaMenu